import { useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';

const useAuth = () => {
    const [isAuthenticated, setIsAuthenticated] = useState(authService.isAuthenticated());
    const [user, setUser] = useState(authService.getCurrentUser());
    const [isLoading, setIsLoading] = useState(true);

    const revalidate = useCallback(async () => {
        setIsLoading(true);
        try {
            // Short-circuit if there is no token at all to avoid unnecessary 401 spam
            if (!authService.getToken()) {
                setIsAuthenticated(false);
                setUser(null);
                // Ensure UI does not get stuck showing spinner when no token
                setIsLoading(false);
                return;
            }
            const { valid, user: validatedUser } = await authService.validateToken();
            setIsAuthenticated(valid);
            setUser(validatedUser);
        } catch (error) { // eslint-disable-line no-unused-vars
            setIsAuthenticated(false);
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        revalidate();
    }, [revalidate]);

    // Listen to auth changes (same-tab and cross-tab) and revalidate
    useEffect(() => {
        const onAuthChanged = () => {
            revalidate();
        };
        const onStorage = (e) => {
            if (e.key === 'authToken' || e.key === 'userInfo') {
                revalidate();
            }
        };
        window.addEventListener('auth-changed', onAuthChanged);
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('auth-changed', onAuthChanged);
            window.removeEventListener('storage', onStorage);
        };
    }, [revalidate]);

    return { isAuthenticated, user, isLoading, revalidate };
};

export default useAuth;